import { Container, NarrowContainer } from "shared/UI/LayoutComponents/LayoutComponents";
import Stepper from "shared/UI/Stepper/Stepper";
import { PageHeader } from "UIPalette/PageHeader/PageHeader";
import { ReactComponent as CloseCircle } from "@icons/close-circle.svg";
import { ReactComponent as ResetIcon } from "shared/assets/Icons/reset.svg";
import { Textarea } from "shared/UI/Textarea/Textarea";
import Button from "shared/UI/Buttons/Button";
import { useMatchMedia } from "shared/UI/Hooks/useMatchMedia";
import { ResponsiveBreakpoints } from "shared/UI/ResponsiveBreakpoints";
import { PropsWithChildren, useState } from "react";
import classNames from "classnames";
import { Controller, useForm } from "react-hook-form";
import { useNavigate, useLocation } from "react-router-dom";
import { JournalAttributes } from "../JournalAttributes/JournalAttributes";
import {
  JournalAttribute,
  JournalEntry,
  useCreateJournalEntryFunctionRequest,
  useEditJournalEntryFunctionRequest,
  useGetAllJournalEntriesFunctionRequest,
  useGetJournalEntryFunctionRequest,
  useListAttributesFunctionRequest,
} from "shared/request/myHealthyAdvantageApi";
import { ClientInfoState } from "shared/core/state/clientInfoState";
import { PageLoader } from "UIPalette/PageLoader/PageLoader";
import { RetryButton } from "UIPalette/Buttons/RetryButton";
import { useTranslation } from "react-i18next";
import { useNotifyModalContext } from "shared/core/state/NotifyModalContext/NotifyModalContext";
import { JournalLogged } from "../JournalLogged/JournalLogged";
import { trackEvents } from "core/monitoring/Events";
import { EventNames } from "core/monitoring/types/enums";

const MobileContainer = ({ children }: PropsWithChildren) => <div className="pt-10">{children}</div>;

const ChildrenContainer = ({ children }: PropsWithChildren) => <div className="mx-auto max-w-[500px] px-6">{children}</div>;

type Inputs = {
  Content: string;
};

export const JournalForm = ({ handleMainBack, initialValue }: { handleMainBack: () => void; initialValue?: JournalEntry }) => {
  const { t } = useTranslation(undefined, { keyPrefix: "positiveYou.journalForm" });
  const isMobileApp = !!window.flutter_inappwebview;
  const navigate = useNavigate();
  const location = useLocation();
  const xClientCode = ClientInfoState.getCode();
  const isLargerDisplay = useMatchMedia(ResponsiveBreakpoints.Desktop);
  const [activeStep, setActiveStep] = useState(0);
  const { data: state, mutate: getCurrentJournalEntry } = useGetJournalEntryFunctionRequest(
    {
      xClientCode,
      id: initialValue?.id,
    },
    { shouldFetch: !!initialValue }
  );
  const {
    data: groupsData,
    isLoading,
    error,
    mutate,
  } = useListAttributesFunctionRequest({
    xClientCode
  });
  const { modalNotify } = useNotifyModalContext();
  const { trigger: createJournalEntry } = useCreateJournalEntryFunctionRequest();
  const { trigger: editJournalEntry } = useEditJournalEntryFunctionRequest();
  
  const { mutate: mutateJournalEntries } = useGetAllJournalEntriesFunctionRequest({
    xClientCode
  });

  const [selectedAttributes, setSelectedAttributes] = useState<JournalAttribute[]>(initialValue?.attributes ?? []);
  const [showJournalLogged, setShowJournalLogged] = useState(false);
  const [showContentError, setShowContentError] = useState(false);
  const [createdEntry, setCreatedEntry] = useState<JournalEntry>();

  const isFinalStep = activeStep === 1;

  const PageContainer = isLargerDisplay ? Container : MobileContainer;

  const FormContainer = isLargerDisplay ? NarrowContainer : ChildrenContainer;

  const formHeight = isLargerDisplay ? "min-h-[450px]" : "min-h-[55vh]";

  const showResetButton = selectedAttributes.length > 0;

  const handleResetAttributes = () => {
    setSelectedAttributes([]);
  };

  const {
    handleSubmit,
    control,
    getValues,
    formState: { isSubmitting, isDirty },
  } = useForm<Inputs>({
    defaultValues: {
      Content: initialValue?.content ?? "",
    },
  });

  async function onSubmit({ Content }: Inputs) {
    const date = location.state?.date ? new Date(location.state.date): new Date();
    const body = {
      Content,
      Attributes: selectedAttributes.map((attribute) => attribute.id),
      Date: {
        year: date.getFullYear(),
        month: date.getMonth() + 1,
        day: date.getDate(),
       },
    };
    
    try {
      if (!!initialValue) {
        trackEvents(EventNames.EDIT_POSITIVE_YOU);
        await editJournalEntry({ body: { Id: initialValue.id, ...body } });
        await getCurrentJournalEntry();
      } else {
        trackEvents(EventNames.CREATE_POSITIVE_YOU);
        await createJournalEntry({ body });
        const freshEntries = await mutateJournalEntries();
        const createdJournalEntry = freshEntries?.data.entries.find((entry) => entry.date === date.toDateOnlyString());
        setCreatedEntry(createdJournalEntry);
      }
      setShowJournalLogged(true);
    } catch (err: any) {
      const errorMessage = err.status === 400 ? t("error400") : t("errorMessage");
      modalNotify("error", t("errorTitle"), errorMessage, t("errorCta"));
    }
  }

  const handleBack = () => {
    if (activeStep === 0) {
      handleMainBack();
    } else {
      setActiveStep((prev) => prev - 1);
    }
  };

  const handleNext = () => {
    if (getValues("Content").trim() === "") {
      setShowContentError(true);
      return;
    }
    setActiveStep((prev) => prev + 1);
  };

  const journalAttributeChips = () => {
    if (isLoading || !!error)
      return (
        <div className="flex-1 h-full flex items-center justify-center">
          {isLoading ? <PageLoader title={t("attributesLoading")} /> : <RetryButton text={t("attributesError")} onClick={mutate} />}
        </div>
      );

    return <JournalAttributes data={groupsData} selectedAttributes={selectedAttributes} handleSelectedAttributes={setSelectedAttributes} />;
  };

  const handleNavigation = () => {
    if (!!initialValue) {
      navigate("/positive-you-journal/entry", {
        state,
        replace: true,
      });
    } else if (createdEntry) {
      navigate("/positive-you-journal/entry", {
        state: createdEntry,
        replace: true,
      });
    } else {
      navigate("/positive-you-journal", { replace: true });
    }
  };

  if (showJournalLogged) return <JournalLogged handleNavigation={handleNavigation} />;

  return (
    <PageContainer>
      <div className="w-full mx-auto max-w-[500px] lg:max-w-[800px] px-6 lg:px-0">
        <PageHeader hideForMobile onClickBack={handleBack} onClickNext={() => navigate(-1)} nextTitle={t("close")} nextIcon={CloseCircle} />
      </div>
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="space-y-8">
          <div className="space-y-4">
            <FormContainer>
              <Stepper steps={2} activeStep={activeStep} />
            </FormContainer>

            {activeStep === 0 && (
              <FormContainer>
                <Controller
                  name={"Content"}
                  control={control}
                  rules={{
                    validate: {
                      required: (v: string) => v !== "" || t("contentError"),
                    },
                  }}
                  render={({ field: { onChange, value } }) => (
                    <div className="text-font-dark">
                      <Textarea
                        id="Content"
                        placeholder={t("contentPlaceholder")}
                        aria-label={t("contentLabel")}
                        onChange={(e) => {
                          onChange(e);
                          if (showContentError && e.target.value !== "") {
                            setShowContentError(false);
                          } else if (!showContentError && e.target.value === "") {
                            setShowContentError(true);
                          }
                        }}
                        value={value}
                        className={classNames(formHeight)}
                        helperText={showContentError ? t("contentError") : undefined}
                        state={showContentError ? "error" : undefined}
                      />
                    </div>
                  )}
                />
              </FormContainer>
            )}
        {activeStep === 1 && (
          <div className={classNames("flex flex-col space-2", formHeight)}>
            <FormContainer>
              <p>{t("attributesTitle")}</p>
              <div className="flex h-5 items-center justify-end">
                {showResetButton && (
                  <Button
                    variant="text"
                    text={
                      <div className="flex items-center gap-2">
                        <ResetIcon
                          aria-hidden="true"
                          className="fill-brand-grey hover:fill-brand-grey-light"
                        />
                        Reset
                      </div>
                    }
                    onClick={handleResetAttributes}
                    className="!w-fit"
                  />
                )}
              </div>
            </FormContainer>
            {journalAttributeChips()}
          </div>
        )}
          </div>
          <FormContainer>
            <div className={classNames("flex flex-col gap-4 items-center", { "pb-4": !isMobileApp })}>
              {isFinalStep && <Button type="submit" disabled={isSubmitting} text={t("submit")} />}
              {!isFinalStep && <Button disabled={(!initialValue && !isDirty) || showContentError} text={t("next")} onClick={handleNext} />}
              {isMobileApp && <Button variant="text" text={t("back")} onClick={handleBack} />}
            </div>
          </FormContainer>
        </div>
      </form>
    </PageContainer>
  );
};
